import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#E84610', // Brand orange
      light: '#ff7043',
      dark: '#483C3B', // Brand dark neutral
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#FAB300', // Brand yellow/gold
      light: '#ffcc02',
      dark: '#c7930a',
      contrastText: '#000000',
    },
    error: {
      main: '#d32f2f',
    },
    warning: {
      main: '#FAB300', // Use brand yellow for warnings
    },
    info: {
      main: '#0288d1',
    },
    success: {
      main:  '#C4EED0',
      contrastText: '#0A3818',
    },
    // Base colors
    common: {
      black: '#000000',
      white: '#FFFFFF',
    },
    // Text colors
    text: {
      primary: '#000000', // Base black
      secondary: '#483C3B', // Brand dark for secondary text
      muted: '#7A706F', // Subdued helper text
      disabled: 'rgba(0, 0, 0, 0.38)',
    },
    // Background colors
    background: {
      default: '#FFFFFF', // Base white
      paper: '#FFFFFF',
      neutral: '#EAE7E6', // Sidebar / neutral background
      subtle: '#F4F2F1', // Content area warm grey
    },
    // Gray scale with your provided grays
    grey: {
      25: '#FAFEFB', // Secondary gray 25
      50: '#DEDFD1', // Secondary gray 50
      100: '#f8f9fa',
      150: '#C5BFBE', // Connector / divider neutral
      200: '#D5CFCE', // Inactive icon background
      300: '#e0e0e0',
      400: '#EAE7E6', // Your existing neutral
      500: '#483C3B', // Brand dark neutral
      900: '#000000', // Base black
    },
    // Custom brand colors for specific use cases
    brand: {
      orange: '#E84610',
      darkNeutral: '#483C3B',
      yellow: '#FAB300',
    },
  },
  typography: {
    fontFamily: 'Readex Pro, sans-serif',
    // Custom typography variants that match your dashboard
    h4: {
      fontSize: '24px',
      fontWeight: 300,
      color: '#000000', // Base black
      lineHeight: 1.2,
    },
    h6: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#000000', // Base black
    },
    body1: {
      fontSize: '14px',
      fontWeight: 300,
      color: '#000000', // Base black
    },
    body2: {
      fontSize: '14px',
      fontWeight: 400,
      color: '#483C3B', // Brand dark for secondary body text
      lineHeight: '20px',
    },
    displaySm: {
      fontSize: '24px',
      lineHeight: '32px',
      fontWeight: 500,
    },
    dialogTitle: {
      margin: 0,
      fontSize: '24px',
      fontWeight: 600,
      color: '#000000',
      fontFamily: 'Readex Pro, sans-serif',
    },
    dialogLabel: {
      display: 'block',
      marginBottom: '8px',
      fontSize: '14px',
      fontWeight: 500,
      color: '#483C3B',
      fontFamily: 'Readex Pro, sans-serif',
    },
    md: {
      fontSize: '14px',
      fontWeight: 400,
    },
    sm: {
      fontSize: '12px',
      lineHeight: '18px',
      fontWeight: 300,
    },
    xl: {
      fontSize: '18px',
      lineHeight: '28px',
      fontWeight: 500
    },
    // Custom variants for your specific use cases
    tableHeader: {
      fontSize: '14px',
      fontWeight: 600,
      color: '#000000', // Base black
    },
    tableCell: {
      fontSize: '14px',
      fontWeight: 400,
      color: '#000000', // Base black
    },
    navItem: {
      fontSize: '14px',
      fontWeight: 300,
      lineHeight: '20px',
      letterSpacing: '0%',
      color: '#483C3B', // Brand dark for navigation
    },
    dialogHelperText: {
      fontSize: '13px',
      lineHeight: '1.4',
      color: '#483C3B',
    },
    aiCategoryLabel: {
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '20px',
      display: 'block',
    },
    aiCategoryDescription: {
      fontSize: '12px',
      lineHeight: '18px',
      color: '#7A706F',
      display: 'block',
    },
    aiSectionHeading: {
      fontSize: '20px',
      fontWeight: 700,
    },
    aiSectionSubtitle: {
      fontSize: '13px',
      color: '#7A706F',
    },
    cvSectionLabel: {
      fontSize: '15px',
      lineHeight: '22px',
    },
    headerTitle: {
      fontSize: '30px',
      lineHeight: '38px',
      fontWeight: 500,
    },
    headerSubtitle: {
      fontSize: '18px',
      lineHeight: '28px',
      fontWeight: 300,
    },
    fieldLabel: {
      fontSize: '12px',
      display: 'block',
      marginBottom: '4px',
      color: '#483C3B',
    },
    legalSubheading: {
      fontSize: '14px',
      fontWeight: 500,
      display: 'block',
    },
  },
  shape: {
    borderRadius: 8,
    pill: 20,
  },
  layout: {
    headerHeight: 90,    // CVHeader bar
    navbarHeight: 65,    // App top navbar
    sidebarWidth: 320,   // CV builder sidebar
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          const base = {
            textTransform: 'none',
            fontFamily: 'Readex Pro, sans-serif',
          };
          // Dialog action buttons (cancel / create) - shared base, variant controls appearance
          if (ownerState['data-dialog-action']) {
            const actionType = ownerState['data-action-type'] ?? 'cancel';
            const shared = {
              ...base,
              padding: '12px 24px',
              borderRadius: '8px',
              textTransform: 'uppercase',
              fontWeight: 600,
              transition: 'all 0.2s ease',
            };
            if (actionType === 'create') {
              return {
                ...shared,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.common.white,
                border: 'none',
                transition: 'background-color 0.2s ease',
                '&:hover': { backgroundColor: theme.palette.primary.dark },
                '&.Mui-disabled': {
                  backgroundColor: theme.palette.grey[50],
                  color: theme.palette.grey[500],
                },
              };
            }
            return {
              ...shared,
              border: `1px solid ${theme.palette.grey[50]}`,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.secondary,
              '&:hover': {
                borderColor: theme.palette.grey[300],
                backgroundColor: theme.palette.grey[25],
              },
            };
          }
          // Selectable / toggle buttons - data-size: "full" (block) | "compact" (chip)
          if (ownerState['data-selectable']) {
            const selected = ownerState['data-selected'];
            const size = ownerState['data-size'] ?? 'compact';
            if (size === 'full') {
              return {
                ...base,
                width: '100%',
                height: 48,
                borderRadius: '8px',
                backgroundColor: theme.palette.background.neutral,
                border: selected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.grey[50]}`,
                color: theme.palette.text.secondary,
                fontWeight: 600,
                fontSize: '14px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                '&:hover': selected ? {} : {
                  borderColor: theme.palette.grey[300],
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                },
              };
            }
            return {
              ...base,
              border: selected ? 'none' : `1px solid ${theme.palette.grey[50]}`,
              borderRadius: '20px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: 500,
              backgroundColor: selected ? theme.palette.background.neutral : 'transparent',
              color: selected ? theme.palette.text.primary : theme.palette.text.secondary,
              transition: 'all 0.2s ease',
              outline: 'none',
              '&:hover': selected ? {} : { backgroundColor: theme.palette.grey[25] },
            };
          }
          if (ownerState['data-tc'] === 'back') {
            return { ...base, color: theme.palette.text.secondary };
          }
          // Dark/filled inline SAVE button (CV builder sections: Courses, WorkExperiences, Education, Certifications)
          if (ownerState['data-tc'] === 'save') {
            return {
              ...base,
              backgroundColor: theme.palette.primary.dark,
              color: theme.palette.common.white,
              borderRadius: '6px',
              padding: '6px 24px',
              fontWeight: 700,
              letterSpacing: '0.8px',
              '&:hover': { backgroundColor: '#332B2A' },
              '&.Mui-disabled': { backgroundColor: '#9E9E9E', color: theme.palette.common.white },
            };
          }
          return base;
        },
        contained: {
          backgroundColor: '#E84610', // Brand orange
          color: '#FFFFFF', // Base white
          boxShadow: '0px 3px 1px -2px #00000033, 0px 2px 2px 0px #00000024, 0px 1px 5px 0px #0000001F',
          '&:hover': {
            backgroundColor: '#483C3B', // Brand dark on hover
            boxShadow: '0px 3px 1px -2px #00000033, 0px 2px 2px 0px #00000024, 0px 1px 5px 0px #0000001F',
          },
        },
        outlined: {
          borderColor: '#DEDFD1', // Secondary gray 50
          color: '#483C3B', // Brand dark
          '&:hover': {
            borderColor: '#483C3B',
            backgroundColor: '#FAFEFB', // Secondary gray 25
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: 'Readex Pro, sans-serif',
          fontSize: '14px',
          borderBottom: '1px solid #DEDFD1', // Secondary gray 50
          padding: '12px 16px',
        },
        head: {
          fontWeight: 600,
          color: '#000000', // Base black
          backgroundColor: '#FAFEFB', // Secondary gray 25
        },
        body: {
          fontWeight: 400,
          color: '#000000', // Base black
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          fontFamily: 'Readex Pro, sans-serif',
          ...(ownerState['data-tc'] === 'page-title' && { fontWeight: 600 }),
          ...(ownerState['data-tc'] === 'subtitle' && { color: theme.palette.text.secondary }),
          ...(ownerState['data-tc'] === 'footer' && {
            color: theme.palette.text.secondary,
            textAlign: 'center',
          }),
          ...(ownerState['data-tc'] === 'unsaved-warning' && {
            fontSize: '12px',
            color: theme.palette.primary.main,
          }),
        }),
      },
    },
    MuiBox: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          switch (ownerState['data-tc']) {
            case 'page': return { backgroundColor: theme.palette.background.neutral };
            case 'card': return { backgroundColor: theme.palette.background.paper };
            case 'contact': return { color: theme.palette.primary.main };
            default: return {};
          }
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ ownerState }) => {
          const base = {
            '& .MuiOutlinedInput-root': {
              fontFamily: 'Readex Pro, sans-serif',
              fontSize: '14px',
              '& fieldset': { borderColor: '#DEDFD1' },
              '&:hover fieldset': { borderColor: '#483C3B' },
              '&.Mui-focused fieldset': { borderColor: '#E84610' },
            },
          };
          if (ownerState['data-dialog-input']) {
            return {
              '& .MuiOutlinedInput-root': {
                fontFamily: 'Readex Pro, sans-serif',
                fontSize: '14px',
                backgroundColor: '#FAFEFB',
                borderRadius: '8px',
                '& fieldset': {
                  borderColor: '#DEDFD1',
                  borderRadius: '8px',
                },
                '&:hover fieldset': { borderColor: '#DEDFD1' },
                '&.Mui-focused fieldset': { borderColor: '#E84610' },
                '& .MuiOutlinedInput-input': { padding: '12px' },
              },
            };
          }
          return base;
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontFamily: 'Readex Pro, sans-serif',
          fontSize: '14px',
        },
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          '& .MuiPaginationItem-root': {
            fontFamily: 'Readex Pro, sans-serif',
            fontSize: '14px',
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontFamily: 'Readex Pro, sans-serif',
          fontWeight: 300,
          fontSize: '14px',
          lineHeight: '20px',
          letterSpacing: '0%',
          color: '#483C3B', // Brand dark
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#FAFEFB',
          borderRadius: '8px',
          height: '100%',
        },
      },
      defaultProps: {
        elevation: 0,
      },
    },
    // Paper component styling
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF', // Base white
          borderRadius: '8px',
        },
        // Don't apply border radius to certain Paper components
        '&.MuiTableContainer-root': {
          borderRadius: '8px',
          boxShadow: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '24px',
          padding: '8px',
        },
      },
    },
    // AppBar component styling - override Paper border radius
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: '0px', // Remove rounded corners from AppBar
          backgroundColor: '#483C3B', // Brand dark neutral
        },
      },
    },
    MuiAccordion: {
      variants: [
        {
          props: { variant: 'aiCategory' },
          style: {
            border: '1px solid #E0DBD9',
            borderRadius: '4px !important',
            marginBottom: '8px',
            boxShadow: 'none',
            '&:before': { display: 'none' },
          },
        },
      ],
    },
    MuiAccordionSummary: {
      variants: [
        {
          props: { variant: 'aiCategory' },
          style: {
            minHeight: '48px',
            '& .MuiAccordionSummary-content': { marginTop: '10px', marginBottom: '10px' },
          },
        },
      ],
    },
    MuiAccordionDetails: {
      variants: [
        {
          props: { variant: 'aiCategory' },
          style: { paddingTop: 0, paddingBottom: '16px', paddingLeft: '16px', paddingRight: '16px' },
        },
      ],
    },
    MuiChip: {
      variants: [
        {
          props: { variant: 'aiLevel' },
          style: {
            backgroundColor: '#E84610',
            color: '#fff',
            fontWeight: 600,
            fontSize: '12px',
            height: '24px',
            flexShrink: 0,
          },
        },
        {
          props: { variant: 'aiSkill' },
          style: {
            backgroundColor: '#F5F3F2',
            fontSize: '11px',
          },
        },
      ],
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          const style = ownerState['data-icon-style'];
          if (style === 'close') {
            return {
              backgroundColor: theme.palette.grey[25],
              border: 'none',
              borderRadius: '50%',
              width: 32,
              height: 32,
              transition: 'background-color 0.2s ease',
              '&:hover': { backgroundColor: theme.palette.grey[50] },
            };
          }
          if (style === 'action') {
            return { color: theme.palette.text.secondary };
          }
          return {};
        },
      },
    },
  },
});

export default theme;